import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import TopNav from "@/components/TopNav";
import { ChatCircleText, ArrowLeft, EnvelopeOpen, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminFeedback() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/feedback");
      setFeedback(data);
    } catch (e) {
      if (e.response?.status === 403) {
        setErr("You need to be the owner or admin of at least one circle to view feedback.");
      } else {
        setErr("Couldn't load feedback.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    // Optimistic update.
    setFeedback(prev => prev.map(f => f.id === id ? { ...f, is_read: true } : f));
    try {
      await api.post(`/feedback/${id}/read`);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't mark as read");
      load();
    }
  };

  const unreadCount = feedback.filter(f => !f.is_read).length;

  return (
    <div className="min-h-screen bg-ivory grain-bg">
      <TopNav />
      <main className="max-w-4xl mx-auto px-5 md:px-10 py-10" data-testid="admin-feedback-page">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-sageGray hover:text-forest inline-flex items-center gap-1.5 mb-3"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-forest text-gold flex items-center justify-center">
              <ChatCircleText size={22} weight="duotone" />
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-forest">Feedback inbox</h1>
            {unreadCount > 0 && (
              <Badge className="bg-gold/20 text-forest border border-gold/40 rounded-full text-[11px] uppercase tracking-widest" data-testid="inbox-unread-count">
                {unreadCount} new
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sageGray max-w-2xl mb-8">
          Every note sent by a circle member, newest first. Use it to spot recurring requests, bugs and ideas worth promoting.
        </p>
        <div className="gold-divider mb-8" />

        {loading && <p className="text-sageGray" data-testid="feedback-loading">Loading…</p>}
        {err && <div className="legacy-card p-8 text-center text-sageGray" data-testid="feedback-error">{err}</div>}
        {!loading && !err && feedback.length === 0 && (
          <div className="legacy-card p-10 text-center text-sageGray" data-testid="feedback-empty">
            No feedback yet. Members will see a floating button on every page once they're logged in.
          </div>
        )}
        {!loading && !err && feedback.length > 0 && (
          <ul className="space-y-4" data-testid="feedback-list">
            {feedback.map((f) => (
              <li
                key={f.id}
                className={`legacy-card p-5 transition-colors ${f.is_read ? "bg-white" : "bg-gold/5 border-gold/40"}`}
                data-testid={`feedback-item-${f.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-display text-lg text-forest truncate" data-testid={`feedback-author-${f.id}`}>
                      {f.user_name || "Anonymous"}
                    </p>
                    {f.is_read ? (
                      <Badge className="bg-cream text-sageGray border border-sage rounded-full text-[10px] uppercase tracking-widest" data-testid={`feedback-status-read-${f.id}`}>
                        Read
                      </Badge>
                    ) : (
                      <Badge className="bg-gold text-forest border-0 rounded-full text-[10px] uppercase tracking-widest" data-testid={`feedback-status-new-${f.id}`}>
                        <Sparkle size={10} weight="fill" className="mr-1" /> New
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-sageGray shrink-0" data-testid={`feedback-date-${f.id}`}>
                    {new Date(f.created_at).toLocaleString()}
                  </p>
                </div>
                <p className="text-charcoal whitespace-pre-line leading-relaxed" data-testid={`feedback-message-${f.id}`}>
                  {f.message}
                </p>
                <div className="flex items-center justify-between gap-4 mt-3 flex-wrap">
                  {f.user_email ? (
                    <Link to={`mailto:${f.user_email}`} className="text-xs text-sageGray hover:text-forest underline-offset-2 hover:underline">
                      {f.user_email}
                    </Link>
                  ) : <span />}
                  {!f.is_read && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markRead(f.id)}
                      className="border-forest/40 text-forest hover:bg-cream rounded-full"
                      data-testid={`feedback-mark-read-${f.id}`}
                    >
                      <EnvelopeOpen size={14} className="mr-1.5" weight="bold" /> Mark as read
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
